import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QuotationSnapshot } from '../../../shared/api/quotations.api'
import type { RequestItem } from '../../../shared/api/types'
import { bulkClearDealStatus } from '../../../shared/api/requestItems.api'
import { toastOrderAfterClear } from './dealOrderToast'
import { toast } from '../../../shared/ui/toastStore'
import { confirm } from '../../../shared/ui/confirmStore'

interface Props {
  requestId: string
  /** Список КП заявки (из ['quotations', requestId]) — всегда актуальный. */
  quotations: QuotationSnapshot[]
  /** Позиции заявки — чтобы посчитать сумму уже оформленных. */
  items: RequestItem[]
  /**
   * Подтверждение. quotationId === null → закрыть заявку, оставив ручную
   * разметку позиций как есть. quotationId: string → весь заказ оформляется
   * по этому КП (все его позиции уходят в ORDERED по его ценам).
   */
  onConfirm: (quotationId: string | null) => Promise<void> | void
  /** Закрытие без оформления. */
  onCancel: () => void
}

const fmtMoney = (n: number) => Math.round(n).toLocaleString('ru-RU')
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })

// 'manual' — оставить ручную разметку позиций. Иначе — id выбранного КП.
type Choice = 'manual' | string

/**
 * Оформление заявки в заказ (кнопка «Сделка ✓» в ActionsPanel).
 *
 * Менеджер выбирает, чем оформить заказ:
 *  - «Оставить ручную разметку» — в заказ войдут позиции, которые менеджер
 *    сам отметил «В заказ» в деталях КП (могут быть из разных КП, у каждой
 *    своя цена). Доступно, только если такие позиции есть.
 *  - Конкретное КП — весь заказ оформляется по нему: все позиции этого КП
 *    уходят в ORDERED по его ценам. Прежняя разметка (отказы, заказ из
 *    другого КП) перезаписывается. Сумма заказа = сумма этого КП.
 *
 * Список КП приходит из общего кэша ['quotations', requestId] — если КП
 * удалили/обновили, модалка показывает актуальный список.
 */
export function WonQuotationModal({ requestId, quotations, items, onConfirm, onCancel }: Props) {
  const queryClient = useQueryClient()
  const orderedItems = items.filter((i) => i.status === 'ORDERED')
  // Сумма ручной разметки — уже оформленные позиции по их зафиксированным ценам.
  const manualSum = orderedItems.reduce((s, i) => s + (i.orderedPrice ?? 0) * i.quantity, 0)
  const hasManual = orderedItems.length > 0

  // Свежие КП — сверху.
  const sorted = [...quotations].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )

  // Дефолт: есть ручная разметка → предлагаем оставить её. Иначе — свежее КП.
  const [choice, setChoice] = useState<Choice>(hasManual ? 'manual' : (sorted[0]?.id ?? 'manual'))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  // Esc, которым пользователь закрывает НАШ confirm снятия «Счёта», не должен
  // заодно закрывать модалку оформления (ревью 29.07) — пока confirm открыт,
  // Esc модалки игнорируется.
  const confirmOpenRef = useRef(false)

  // Если выбранное КП исчезло из списка (удалили, пока модалка открыта) —
  // сбрасываем выбор на ручную разметку, чтобы не отправить мёртвый id.
  useEffect(() => {
    if (choice !== 'manual' && !quotations.some((q) => q.id === choice)) {
      setChoice('manual')
    }
  }, [quotations, choice])

  // Выбранное КП и расчёт суммы заказа по нему.
  const selectedQuotation = choice !== 'manual' ? quotations.find((q) => q.id === choice) : null
  // Позиции, оформленные в заказ, но отсутствующие в выбранном КП — они
  // останутся в заказе со своей ценой (каскад их не трогает). В обычном
  // случае (КП на все позиции заявки) этот список пуст.
  const kpItemIds = new Set(selectedQuotation?.items?.map((si) => si.itemId) ?? [])
  const orderedOutside = selectedQuotation ? orderedItems.filter((i) => !kpItemIds.has(i.id)) : []
  const outsideSum = orderedOutside.reduce((s, i) => s + (i.orderedPrice ?? 0) * i.quantity, 0)

  const totalSum = selectedQuotation ? selectedQuotation.totalSum + outsideSum : manualSum

  const handleConfirm = async () => {
    const quotationId = choice === 'manual' ? null : choice
    setSaving(true)
    setError('')
    try {
      await onConfirm(quotationId)
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось оформить заказ'
      setError(msg)
      setSaving(false)
    }
  }

  // Снятие «Счёт» с позиций, оформленных вне выбранного КП (29.07): раньше их
  // можно было снять только из деталей живого КП — «осиротевшие» после удаления
  // КП-источника застревали в заказе навсегда и задваивали сумму сделки.
  const clearOutsideMut = useMutation({
    mutationFn: () => bulkClearDealStatus(requestId, { itemIds: orderedOutside.map((i) => i.id) }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      void queryClient.invalidateQueries({ queryKey: ['quotations', requestId] })
      void queryClient.invalidateQueries({ queryKey: ['order-by-request', requestId] })
      toast.success(`Статус «Счёт» снят с ${res.updated} поз. — они вернулись в «КП создано»`)
      toastOrderAfterClear(res)
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось снять статус «Счёт»'
      toast.error(msg)
    },
  })
  const handleClearOutside = async () => {
    confirmOpenRef.current = true
    try {
      const ok = await confirm({
        title: `Снять «Счёт» с ${orderedOutside.length} поз. вне этого КП?`,
        message:
          'Позиции вернутся в статус «КП создано» и не попадут в заказ. Позиции выбранного КП не изменятся.',
        confirmText: 'Снять',
        danger: true,
      })
      if (ok) clearOutsideMut.mutate()
    } finally {
      confirmOpenRef.current = false
    }
  }

  // Пока идёт снятие «Счёта» — блокируем оформление/закрытие/выбор, чтобы две
  // конкурирующие мутации не закрыли сделку по разметке, которая сейчас
  // снимается (ревью 29.07).
  const busy = saving || clearOutsideMut.isPending

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy && !confirmOpenRef.current) onCancel()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onCancel, busy])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel()
      }}
    >
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Выставить счёт по заявке</h3>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-40"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Чем оформить заказ
          </div>

          {/* Вариант 1: оставить ручную разметку (если позиции уже размечены) */}
          {hasManual && (
            <label
              className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                choice === 'manual'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="won-choice"
                checked={choice === 'manual'}
                onChange={() => setChoice('manual')}
                disabled={busy}
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-gray-800">
                    Оставить ручную разметку
                  </span>
                  <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                    {fmtMoney(manualSum)} ₽
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {orderedItems.length} поз. «Счёт» по их КП
                </div>
              </div>
            </label>
          )}

          {sorted.length > 0 && (
            <div className="text-xs text-gray-400 px-1">
              {hasManual ? '…или оформить весь заказ по одному КП:' : 'Весь заказ по одному КП:'}
            </div>
          )}

          {/* Вариант 2: весь заказ по выбранному КП */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {sorted.map((q) => {
              const itemsCount = q.items?.length ?? 0
              const selected = q.id === choice
              return (
                <label
                  key={q.id}
                  className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    selected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="won-choice"
                    checked={selected}
                    onChange={() => setChoice(q.id)}
                    disabled={busy}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800">
                        КП от {fmtDate(q.createdAt)}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {fmtMoney(q.totalSum)} ₽
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      весь заказ — {itemsCount} поз. по ценам этого КП
                      {q.createdBy?.name ? ` · ${q.createdBy.name}` : ''}
                    </div>
                  </div>
                </label>
              )
            })}
          </div>

          {sorted.length === 0 && !hasManual && (
            <p className="text-sm text-gray-600">
              У заявки нет ни одного КП и нет оформленных позиций — оформлять заказ не из чего.
              Сначала создайте КП.
            </p>
          )}

          {/* Позиции вне выбранного КП, которые останутся в заказе */}
          {orderedOutside.length > 0 && (
            <div className="text-xs text-amber-600 px-1">
              + {orderedOutside.length} поз. оформлены в заказ вне этого КП и останутся в заказе на{' '}
              {fmtMoney(outsideSum)} ₽{' '}
              <button
                type="button"
                onClick={() => void handleClearOutside()}
                disabled={busy}
                className="text-red-600 hover:text-red-700 underline disabled:opacity-50"
                title="Вернуть эти позиции в «КП создано» — они не попадут в заказ"
              >
                {clearOutsideMut.isPending ? 'снимаю…' : 'снять «Счёт» с них'}
              </button>
            </div>
          )}

          {/* Итоговая сумма */}
          <div className="flex items-baseline justify-between border-t border-gray-100 pt-2">
            <span className="text-sm font-medium text-gray-700">Итого в заказе:</span>
            <span className="text-base font-semibold text-gray-900">{fmtMoney(totalSum)} ₽</span>
          </div>
          {totalSum === 0 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Сумма заказа будет 0 ₽. Проверьте, что выбрали нужное КП.
            </div>
          )}

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={() => void handleConfirm()}
            disabled={busy}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Выставление…' : 'Выставить счёт'}
          </button>
        </div>
      </div>
    </div>
  )
}
